import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  Grid,
  Skeleton,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  alpha,
  Menu,
  Checkbox
} from '@mui/material';
import { parseHmsToSeconds } from '../../utils/breakDurationHms';
import { setBreakSession } from '../../utils/breakSessionStorage';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Description as DocumentIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Work as WorkIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  Upload as UploadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Groups as InterviewIcon,
  FactCheck as RefCheckIcon,
  Handshake as OfferIcon,
  FreeBreakfast as BreakIcon,
  School as TrainingIcon,
  Task as AdminIcon,
  Close as CloseIcon,
  LocalShipping as VendorCallIcon,
  Schedule as ScheduleIcon,
  ViewColumn as ViewColumnIcon
} from '@mui/icons-material';
import api from '../../utils/api';
import CallAccountContactPicker from '../../components/common/CallAccountContactPicker';

// Icon mapping for activities
const getActivityIcon = (module, action) => {
  if (action === 'login') return <LoginIcon sx={{ fontSize: 18 }} />;
  if (action === 'logout') return <LogoutIcon sx={{ fontSize: 18 }} />;
  if (action === 'idle_time_reported') return <ScheduleIcon sx={{ fontSize: 18 }} />;
  if (action === 'idle_time_resumed') return <LoginIcon sx={{ fontSize: 18 }} />;
  if (action === 'upload') return <UploadIcon sx={{ fontSize: 18 }} />;
  if (action === 'download') return <DownloadIcon sx={{ fontSize: 18 }} />;
  if (action === 'create') return <AddIcon sx={{ fontSize: 18 }} />;
  if (action === 'update') return <EditIcon sx={{ fontSize: 18 }} />;
  if (action === 'delete') return <DeleteIcon sx={{ fontSize: 18 }} />;
  if (action === 'view') return <ViewIcon sx={{ fontSize: 18 }} />;
  if (action?.includes('call')) return <PhoneIcon sx={{ fontSize: 18 }} />;
  if (action?.includes('email')) return <EmailIcon sx={{ fontSize: 18 }} />;
  if (action === 'meeting') return <InterviewIcon sx={{ fontSize: 18 }} />;
  switch (module) {
    case 'contacts': return <PeopleIcon sx={{ fontSize: 18 }} />;
    case 'accounts': return <BusinessIcon sx={{ fontSize: 18 }} />;
    case 'jobs': return <WorkIcon sx={{ fontSize: 18 }} />;
    case 'documents': return <DocumentIcon sx={{ fontSize: 18 }} />;
    case 'punchcard': return <ScheduleIcon sx={{ fontSize: 18 }} />;
    default: return <ViewIcon sx={{ fontSize: 18 }} />;
  }
};

// Color mapping for activities
const getActivityColor = (action) => {
  switch (action) {
    case 'create': return 'success';
    case 'update': return 'info';
    case 'delete': return 'error';
    case 'upload': return 'primary';
    case 'download': return 'secondary';
    case 'login': return 'success';
    case 'logout': return 'warning';
    case 'idle_time_reported': return 'warning';
    case 'idle_time_resumed': return 'success';
    case 'call_candidate':
    case 'call_client': return 'success';
    case 'call_vendor': return 'warning';
    case 'send_email': return 'info';
    case 'interview': return 'secondary';
    case 'meeting': return 'primary';
    case 'screen_resume': return 'warning';
    case 'submit_resume': return 'info';
    case 'reference_check': return 'default';
    case 'offer_discussion': return 'default';
    case 'admin_task': return 'default';
    case 'break': return 'default';
    case 'training': return 'secondary';
    default: return 'default';
  }
};

// Activity types for quick logging
const quickActivityTypes = [
  { code: 'call_candidate', label: 'Call Candidate', icon: PhoneIcon, color: '#4CAF50' },
  { code: 'call_client', label: 'Call Client', icon: PhoneIcon, color: '#2196F3' },
  { code: 'call_vendor', label: 'Call Vendor', icon: VendorCallIcon, color: '#E65100' },
  { code: 'send_email', label: 'Send Email', icon: EmailIcon, color: '#9C27B0' },
  { code: 'screen_resume', label: 'Screen Resume', icon: DocumentIcon, color: '#FF9800' },
  { code: 'submit_resume', label: 'Submit Resume', icon: SendIcon, color: '#00BCD4' },
  { code: 'interview', label: 'Interview', icon: InterviewIcon, color: '#E91E63' },
  { code: 'reference_check', label: 'Reference Check', icon: RefCheckIcon, color: '#795548' },
  { code: 'offer_discussion', label: 'Offer Discussion', icon: OfferIcon, color: '#607D8B' },
  { code: 'meeting', label: 'Meeting', icon: InterviewIcon, color: '#3F51B5' },
  { code: 'break', label: 'Break', icon: BreakIcon, color: '#8BC34A' },
  { code: 'training', label: 'Training', icon: TrainingIcon, color: '#673AB7' },
  { code: 'admin_task', label: 'Admin Task', icon: AdminIcon, color: '#9E9E9E' }
];

const activityPlaceholders = {
  'call_candidate': 'e.g., Called John Doe about Java Developer position',
  'call_client': 'e.g., Discussed headcount, follow-ups',
  'call_vendor': 'e.g., Rates, support case, contract',
  'send_email': 'e.g., Sent job description to candidates',
  'screen_resume': 'e.g., Reviewed 10 resumes for Data Analyst role',
  'submit_resume': 'e.g., Submitted Mike Smith to XYZ Inc',
  'interview': 'e.g., Phone screening with Sarah for PM role',
  'reference_check': 'e.g., Called previous employer - positive feedback',
  'offer_discussion': 'e.g., Discussed salary expectations - $120K',
  'meeting': 'e.g., Team standup — discussed open requirements',
  'break': 'e.g., Lunch break',
  'training': 'e.g., Completed ATS training module',
  'admin_task': 'e.g., Updated candidate database'
};

const MyActivities = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('today');
  const [colMenuAnchorEl, setColMenuAnchorEl] = useState(null);
  const [visibleCols, setVisibleCols] = useState({
    activity: true,
    module: true,
    details: true,
    notes: true,
    time: true
  });
  
  // Log Activity Dialog State
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState(null);
  const [activityNotes, setActivityNotes] = useState('');
  const [contactName, setContactName] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loggingActivity, setLoggingActivity] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [callPick, setCallPick] = useState({ account: null, contact: null });
  const [breakDurationHms, setBreakDurationHms] = useState('00:15:00');
  const visibleColCount =
    (visibleCols.activity ? 1 : 0) +
    (visibleCols.module ? 1 : 0) +
    (visibleCols.details ? 1 : 0) +
    (visibleCols.notes ? 1 : 0) +
    (visibleCols.time ? 1 : 0);

  const fetchActivities = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage
      });
      
      if (moduleFilter !== 'all') {
        params.append('module', moduleFilter);
      }
      
      const response = await api.get(`/activities/my?${params}`);
      setActivities(response.data.data || []);
      setTotalCount(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, moduleFilter]);

  // Fetch contacts for autocomplete
  const fetchContacts = useCallback(async () => {
    try {
      const response = await api.get('/contacts?limit=100&status=active');
      setContacts(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
    fetchContacts();
  }, [fetchActivities, fetchContacts]);

  const isCallClientOrVendor = (code) => code === 'call_client' || code === 'call_vendor';

  // Free-text name for other activity types
  const needsContactName = (activityCode) => {
    return [
      'call_candidate',
      'send_email',
      'interview',
      'submit_resume',
      'reference_check',
      'offer_discussion',
    ].includes(activityCode);
  };

  // Handle activity type selection
  const handleActivityTypeSelect = (activityType) => {
    setSelectedActivityType(activityType);
    setActivityNotes('');
    setContactName('');
    setCallPick({ account: null, contact: null });
    if (activityType?.code === 'break') setBreakDurationHms('00:15:00');
  };

  // Submit activity
  const handleSubmitActivity = async () => {
    if (!selectedActivityType) return;
    if (isCallClientOrVendor(selectedActivityType.code)) {
      if (!callPick.account || !callPick.contact) {
        setSnackbar({ open: true, message: 'Select the company, then the contact you called.', severity: 'error' });
        return;
      }
    }
    if (selectedActivityType.code === 'break') {
      const sec = parseHmsToSeconds(breakDurationHms);
      if (sec == null) {
        setSnackbar({ open: true, message: 'Enter break length as HH:MM:SS (e.g. 00:15:00).', severity: 'error' });
        return;
      }
      if (!String(activityNotes).trim()) {
        setSnackbar({ open: true, message: 'Notes are required for a break.', severity: 'error' });
        return;
      }
    }

    setLoggingActivity(true);
    try {
      if (selectedActivityType.code === 'break') {
        const sec = parseHmsToSeconds(breakDurationHms);
        const breakStartedAt = Date.now();
        const until = breakStartedAt + sec * 1000;
        try {
          await api.post('/punchcard/punch-out');
        } catch (poErr) {
          setSnackbar({
            open: true,
            message: poErr.response?.data?.message || 'Could not punch out for break.',
            severity: 'error'
          });
          return;
        }
        await api.post('/activities/quick', {
          activity_type: 'break',
          notes: String(activityNotes).trim(),
          duration_minutes: Math.max(1, Math.ceil(sec / 60)),
          break_duration_hms: String(breakDurationHms).trim(),
          break_duration_seconds: sec,
          break_planned_end_at: new Date(until).toISOString()
        });
        setBreakSession({ untilMs: until, plannedSeconds: sec, startedAtMs: breakStartedAt });
        window.dispatchEvent(new CustomEvent('erp4cBreakSessionStarted'));
        window.dispatchEvent(new CustomEvent('punchStatusChanged', { detail: { action: 'break-started' } }));
        setLogDialogOpen(false);
        setSelectedActivityType(null);
        setActivityNotes('');
        setContactName('');
        setCallPick({ account: null, contact: null });
        setBreakDurationHms('00:15:00');
        setSnackbar({ open: true, message: 'Break started — punched out. Use the break timer.', severity: 'success' });
        fetchActivities();
        return;
      }

      const notesWithContact = contactName ? `${contactName}: ${activityNotes}`.trim() : activityNotes;

      const body = {
        activity_type: selectedActivityType.code,
        notes: isCallClientOrVendor(selectedActivityType.code) ? activityNotes || null : notesWithContact || null,
        duration_minutes: null,
      };
      if (isCallClientOrVendor(selectedActivityType.code)) {
        body.account_id = callPick.account.id;
        body.contact_id = callPick.contact.id;
      }

      await api.post('/activities/quick', body);

      setLogDialogOpen(false);
      setSelectedActivityType(null);
      setActivityNotes('');
      setContactName('');
      setCallPick({ account: null, contact: null });
      
      setSnackbar({
        open: true,
        message: `${selectedActivityType.label} logged successfully!`,
        severity: 'success'
      });
      
      // Refresh activities
      fetchActivities();
    } catch (error) {
      console.error('Failed to log activity:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to log activity.',
        severity: 'error'
      });
    } finally {
      setLoggingActivity(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatActionLabel = (action) => {
    return action
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const modules = ['all', 'contacts', 'accounts', 'jobs', 'documents', 'recruiting', 'general', 'punchcard', 'auth'];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          My Activities
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setLogDialogOpen(true)}
          sx={{
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            fontWeight: 600
          }}
        >
          Log Activity
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ py: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search activities..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Module"
                value={moduleFilter}
                onChange={(e) => {
                  setModuleFilter(e.target.value);
                  setPage(0);
                }}
              >
                {modules.map((m) => (
                  <MenuItem key={m} value={m}>
                    {m === 'all' ? 'All Modules' : m.charAt(0).toUpperCase() + m.slice(1)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                select
                fullWidth
                size="small"
                label="Period"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Activities Table */}
      <Card>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, pt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<ViewColumnIcon fontSize="small" />}
            onClick={(e) => setColMenuAnchorEl(e.currentTarget)}
            type="button"
          >
            Columns
          </Button>
          <Menu
            anchorEl={colMenuAnchorEl}
            open={Boolean(colMenuAnchorEl)}
            onClose={() => setColMenuAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {[
              ['activity', 'Activity'],
              ['module', 'Module'],
              ['details', 'Details'],
              ['notes', 'Notes'],
              ['time', 'Time']
            ].map(([key, label]) => (
              <MenuItem
                key={key}
                onClick={() => setVisibleCols((prev) => ({ ...prev, [key]: !prev[key] }))}
                dense
              >
                <Checkbox size="small" checked={Boolean(visibleCols[key])} />
                <Typography variant="body2">{label}</Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {visibleCols.activity && <TableCell sx={{ fontWeight: 600 }}>Activity</TableCell>}
                {visibleCols.module && <TableCell sx={{ fontWeight: 600 }}>Module</TableCell>}
                {visibleCols.details && <TableCell sx={{ fontWeight: 600 }}>Details</TableCell>}
                {visibleCols.notes && <TableCell sx={{ fontWeight: 600 }}>Notes</TableCell>}
                {visibleCols.time && <TableCell sx={{ fontWeight: 600 }}>Time</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    {visibleCols.activity && <TableCell><Skeleton /></TableCell>}
                    {visibleCols.module && <TableCell><Skeleton width={80} /></TableCell>}
                    {visibleCols.details && <TableCell><Skeleton /></TableCell>}
                    {visibleCols.notes && <TableCell><Skeleton /></TableCell>}
                    {visibleCols.time && <TableCell><Skeleton width={100} /></TableCell>}
                  </TableRow>
                ))
              ) : activities.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColCount} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No activities found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                activities.map((activity) => (
                  <TableRow key={activity.id} hover>
                    {visibleCols.activity && <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            bgcolor: `${getActivityColor(activity.action)}.main`,
                            color: 'white'
                          }}
                        >
                          {getActivityIcon(activity.module, activity.action)}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatActionLabel(activity.action)}
                          </Typography>
                          {activity.entity_name && (
                            <Typography variant="caption" color="text.secondary">
                              {activity.entity_name}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>}
                    {visibleCols.module && <TableCell>
                      <Chip
                        size="small"
                        label={activity.module}
                        sx={{ textTransform: 'capitalize', fontSize: '0.75rem' }}
                      />
                    </TableCell>}
                    {visibleCols.details && <TableCell>
                      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
                        {activity.description || '-'}
                      </Typography>
                    </TableCell>}
                    {visibleCols.notes && <TableCell>
                      {activity.details?.notes ? (
                        <Typography variant="body2" sx={{ maxWidth: 200, fontStyle: 'italic' }}>
                          "{activity.details.notes}"
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.disabled">-</Typography>
                      )}
                    </TableCell>}
                    {visibleCols.time && <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(activity.performed_at)}
                      </Typography>
                    </TableCell>}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      {/* Log Activity Dialog */}
      <Dialog
        open={logDialogOpen}
        onClose={() => {
          setLogDialogOpen(false);
          setSelectedActivityType(null);
          setCallPick({ account: null, contact: null });
          setActivityNotes('');
          setContactName('');
          setBreakDurationHms('00:15:00');
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            {selectedActivityType ? selectedActivityType.label : 'Log External Activity'}
          </Typography>
          <Button
            onClick={() => {
              setLogDialogOpen(false);
              setSelectedActivityType(null);
              setCallPick({ account: null, contact: null });
              setActivityNotes('');
              setContactName('');
              setBreakDurationHms('00:15:00');
            }}
            size="small"
          >
            <CloseIcon />
          </Button>
        </DialogTitle>
        <DialogContent dividers>
          {!selectedActivityType ? (
            <Grid container spacing={1}>
              {quickActivityTypes.map((activity) => {
                const IconComponent = activity.icon;
                return (
                  <Grid item xs={6} sm={4} key={activity.code}>
                    <Box
                      onClick={() => handleActivityTypeSelect(activity)}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        cursor: 'pointer',
                        textAlign: 'center',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: activity.color,
                          bgcolor: alpha(activity.color, 0.1),
                          transform: 'scale(1.02)'
                        }
                      }}
                    >
                      <IconComponent sx={{ fontSize: 32, color: activity.color, mb: 1 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {activity.label}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Box>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(selectedActivityType.color, 0.1)
                }}
              >
                {React.createElement(selectedActivityType.icon, {
                  sx: { color: selectedActivityType.color, fontSize: 28 }
                })}
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {selectedActivityType.label}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" onClick={() => setSelectedActivityType(null)}>
                  Change
                </Button>
              </Box>

              {selectedActivityType.code === 'break' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                  <Alert severity="warning">
                    Break starts when you submit — you will be <strong>punched out</strong> until you punch back in.
                  </Alert>
                  <Alert severity="info">
                    Return a few seconds before the timer ends so you can punch in on time. After zero, the header clock
                    shows how late you are and an activity can be logged automatically.
                  </Alert>
                  <TextField
                    label="Break length (HH:MM:SS)"
                    fullWidth
                    value={breakDurationHms}
                    onChange={(e) => setBreakDurationHms(e.target.value)}
                    placeholder="00:15:00"
                    helperText="Digital format, max 23:59:59."
                    inputProps={{ spellCheck: false, sx: { fontFamily: 'monospace' } }}
                  />
                </Box>
              )}

              {isCallClientOrVendor(selectedActivityType.code) && (
                <CallAccountContactPicker
                  key={selectedActivityType.code}
                  scope={selectedActivityType.code === 'call_vendor' ? 'vendor' : 'client'}
                  valueAccount={callPick.account}
                  valueContact={callPick.contact}
                  onChange={(acc, c) => setCallPick({ account: acc, contact: c })}
                  disabled={loggingActivity}
                />
              )}

              {needsContactName(selectedActivityType.code) && (
                <Autocomplete
                  freeSolo
                  options={contacts.map((c) => `${c.first_name} ${c.last_name}`)}
                  value={contactName}
                  onChange={(e, value) => setContactName(value || '')}
                  onInputChange={(e, value) => setContactName(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Contact / candidate name"
                      placeholder="Type or select name…"
                      fullWidth
                      sx={{ mb: 2 }}
                    />
                  )}
                />
              )}

              <TextField
                label={selectedActivityType.code === 'break' ? 'Notes (required)' : 'Notes / Details'}
                required={selectedActivityType.code === 'break'}
                placeholder={activityPlaceholders[selectedActivityType.code] || 'Add details about this activity...'}
                multiline
                rows={3}
                fullWidth
                value={activityNotes}
                onChange={(e) => setActivityNotes(e.target.value)}
                helperText={
                  selectedActivityType.code === 'break'
                    ? 'A note is required before starting your break.'
                    : 'What did you do? Who was involved? What was the outcome?'
                }
              />
            </Box>
          )}
        </DialogContent>
        {selectedActivityType && (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setSelectedActivityType(null)}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitActivity}
              disabled={
                loggingActivity ||
                (isCallClientOrVendor(selectedActivityType.code) && (!callPick.account || !callPick.contact)) ||
                (selectedActivityType.code === 'break' &&
                  (!String(activityNotes).trim() || parseHmsToSeconds(breakDurationHms) == null))
              }
              startIcon={loggingActivity ? <CircularProgress size={16} /> : null}
              sx={{ background: `linear-gradient(135deg, ${selectedActivityType.color} 0%, ${selectedActivityType.color}dd 100%)` }}
            >
              {loggingActivity ? 'Logging...' : 'Log Activity'}
            </Button>
          </DialogActions>
        )}
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MyActivities;
